import { Link, useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { ArrowLeft } from "lucide-react";

const SECTIONS = {
  no: [
    {
      title: "1. Behandlingsansvarlig",
      body: (
        <>
          <p>
            Canal Boats Telemark er behandlingsansvarlig for dine personopplysninger i denne
            tjenesten. Kontakt: <a href="mailto:post@canalboats.no" className="underline">post@canalboats.no</a>.
          </p>
        </>
      ),
    },
    {
      title: "2. Hvilke opplysninger vi behandler",
      body: (
        <ul className="list-disc pl-5 space-y-1">
          <li>Konto: navn, e-postadresse, rolle (admin/service/vask/kunde), valgt språk.</li>
          <li>Kunder: bookingdetaljer (datoer, antall personer, eventuelle notater).</li>
          <li>Personell: utfylte sjekklister, hvem som har gjort hva og når.</li>
          <li>Avvik/rapporter: tekstbeskrivelse, bilder, status- og historikkoppføringer.</li>
          <li>Tekniske data: pålogging via krypterte httpOnly-cookies (kun strengt nødvendige
          for innlogging).</li>
        </ul>
      ),
    },
    {
      title: "3. Formål og rettslig grunnlag",
      body: (
        <ul className="list-disc pl-5 space-y-1">
          <li>Levere booking- og portaltjenesten (avtale, GDPR art. 6 nr. 1 b).</li>
          <li>Drift, sikring og forbedring av flåten (berettiget interesse, art. 6 nr. 1 f).</li>
          <li>Lovpålagte plikter knyttet til driftslogger og regnskap (art. 6 nr. 1 c).</li>
        </ul>
      ),
    },
    {
      title: "4. Lagringstid",
      body: (
        <p>
          Kontodata og driftslogger oppbevares så lenge du har en aktiv konto og inntil 12
          måneder etter avsluttet kundeforhold. Avvikslogg holdes i opptil 5 år av sikkerhets-
          og forsikringsårsaker. Bilder slettes når tilhørende avvik er lukket og foreldelses-
          fristen er utløpt.
        </p>
      ),
    },
    {
      title: "5. Mottakere og underleverandører",
      body: (
        <>
          <p>Vi deler ikke personopplysninger med tredjepart utover følgende databehandlere:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Skyleverandør for hosting (servere innenfor EØS).</li>
            <li>Objektlagring for bilder (innenfor EØS, kryptert i ro og i transit).</li>
          </ul>
        </>
      ),
    },
    {
      title: "6. Dine rettigheter",
      body: (
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Innsyn og dataportabilitet:</b> Last ned alle dine data fra "Min profil".</li>
          <li><b>Retting:</b> Endre kontoinformasjon direkte, eller kontakt oss.</li>
          <li><b>Sletting:</b> Slett kontoen din selv fra "Min profil" – audit-logg anonymiseres.</li>
          <li><b>Klage:</b> Du kan klage til Datatilsynet (datatilsynet.no).</li>
        </ul>
      ),
    },
    {
      title: "7. Cookies",
      body: (
        <p>
          Vi bruker kun strengt nødvendige cookies for innlogging (access_token og
          refresh_token). Disse er HttpOnly, Secure og slettes når du logger ut. Vi bruker
          ingen analyse-, sporings- eller markedsføringscookies.
        </p>
      ),
    },
  ],
  en: [
    {
      title: "1. Data Controller",
      body: (
        <p>
          Canal Boats Telemark is the data controller for personal data processed in this
          service. Contact: <a href="mailto:post@canalboats.no" className="underline">post@canalboats.no</a>.
        </p>
      ),
    },
    {
      title: "2. Data we process",
      body: (
        <ul className="list-disc pl-5 space-y-1">
          <li>Account: name, email, role, language preference.</li>
          <li>Customers: booking details (dates, guest count, optional notes).</li>
          <li>Staff: completed checklists with who did what and when.</li>
          <li>Defects/reports: description, photos, status and history entries.</li>
          <li>Technical: encrypted httpOnly login cookies (strictly necessary).</li>
        </ul>
      ),
    },
    {
      title: "3. Purpose and legal basis",
      body: (
        <ul className="list-disc pl-5 space-y-1">
          <li>Providing the booking and portal service (contract, GDPR art. 6(1)(b)).</li>
          <li>Fleet operations, safety and improvement (legitimate interest, art. 6(1)(f)).</li>
          <li>Statutory operational/accounting records (art. 6(1)(c)).</li>
        </ul>
      ),
    },
    {
      title: "4. Retention",
      body: (
        <p>
          Account data and operational logs are kept while your account is active and up to
          12 months after the customer relationship ends. Defect logs are retained for up to
          5 years for safety and insurance reasons. Photos are deleted once the related
          defect is closed and statutory limits expire.
        </p>
      ),
    },
    {
      title: "5. Recipients and sub-processors",
      body: (
        <>
          <p>We do not share personal data with third parties beyond:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Cloud hosting provider (servers within the EEA).</li>
            <li>Object storage for photos (within the EEA, encrypted at rest and in transit).</li>
          </ul>
        </>
      ),
    },
    {
      title: "6. Your rights",
      body: (
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Access & portability:</b> Download all your data from "My profile".</li>
          <li><b>Rectification:</b> Edit your account info or contact us.</li>
          <li><b>Erasure:</b> Delete your account yourself from "My profile" – audit logs are anonymised.</li>
          <li><b>Complaint:</b> You may complain to the Norwegian Data Protection Authority (datatilsynet.no).</li>
        </ul>
      ),
    },
    {
      title: "7. Cookies",
      body: (
        <p>
          We only use strictly necessary cookies for authentication (access_token and
          refresh_token). They are HttpOnly, Secure, and removed on logout. We use no
          analytics, tracking or marketing cookies.
        </p>
      ),
    },
  ],
};

export default function PrivacyPage({ lang: forcedLang }) {
  const navigate = useNavigate();
  const lang = forcedLang || "no";
  const sections = SECTIONS[lang];
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-[#0C1824] text-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><Logo dark size={32} /></Link>
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="privacy-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {lang === "en" ? "Back" : "Tilbake"}
          </Button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39]">
          {lang === "en" ? "Privacy" : "Personvern"}
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#0C1824] mt-1">
          {lang === "en" ? "Privacy Policy" : "Personvernerklæring"}
        </h1>
        <p className="mt-2 text-sm text-[#2B3F4D]">
          {lang === "en"
            ? "How Canal Boats Telemark processes your personal data."
            : "Hvordan Canal Boats Telemark behandler dine personopplysninger."}
        </p>
        <div className="mt-8 space-y-6">
          {sections.map((s) => (
            <section key={s.title}>
              <h2 className="text-lg font-semibold text-[#0C1824]">{s.title}</h2>
              <div className="mt-2 text-sm text-[#2B3F4D] leading-relaxed">{s.body}</div>
            </section>
          ))}
        </div>
        <div className="mt-10 pt-6 border-t border-[#E2E8F0] text-xs text-[#2B3F4D]">
          {lang === "en"
            ? "Last updated: June 2026. Questions? Contact post@canalboats.no."
            : "Sist oppdatert: juni 2026. Spørsmål? Kontakt post@canalboats.no."}
        </div>
      </main>
    </div>
  );
}
